import React, { useEffect, useState, useLayoutEffect } from "react";
import {
   endpointRequest,
   updateServerSession,
} from "../../../../../utilities/requests";
import { ECLTable } from "../../../../index";
import { translate } from "../../../../../utilities/Translate";
import ECLLoader from "../../../../Widgets/ECLLoader/ECLLoader";
import EditableTableCell from "./EditableTableCell";
import debounce from "../../../../../utilities/debounce";
import * as arrow from "apache-arrow";

export default function SpreadSheetTable(props) {
   const [rowData, setRowData] = useState(null);
   const [columns, setColumns] = useState(null);
   const [tableSettings, setTableSettings] = useState(null);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState(null);
   const [tableContainerWidth, setTableContainerWidth] = useState(0);

   async function handleEditCell(rows, columns, rowIndex, columnIndex, newVal) {
      // Update the cell's value
      if (newVal) {
         rows[rowIndex].row[columnIndex] = newVal;
      }
      // Prepare column data arrays, handling `null` values
      const columnData = columns.map((_, colIdx) =>
         rows.map((row) => (row.row[colIdx] !== "" ? row.row[colIdx] : null)),
      );
      // Create Arrow vectors for each column based on column type
      const arrowArrays = columnData.map((data, index) => {
         const colType = columns[index].type.toString();
         switch (colType) {
            case "Utf8": {
               const utf8Array = data.map((value) =>
                  value === null || value === undefined ? null : value,
               );
               return arrow.vectorFromArray(utf8Array, new arrow.Utf8());
            }
            case "Int32": {
               const int32Array = data.map((value) => {
                  if (value === null || value === undefined) {
                     return null; // Handle null or undefined as-is
                  }
                  if (typeof value === "number") {
                     return Math.trunc(value); // Remove the decimal part if it's a float
                  }
                  if (!Number.isInteger(Number(value))) {
                     return; // Invalid value handling
                  }
                  return Number(value); // Return as an integer
               });
               return arrow.vectorFromArray(int32Array, new arrow.Int32());
            }
            case "Int64": {
               const int64Array = data.map((value) => {
                  if (value === null || value === undefined) {
                     return null; // Handle null or undefined as-is
                  }
                  if (typeof value === "number") {
                     return BigInt(Math.trunc(value)); // Truncate floats to integers and convert to BigInt
                  }
                  const numericValue = Number(value); // Convert to a number if it's a string
                  if (isNaN(numericValue)) {
                     return null; // Handle invalid non-numeric values
                  }
                  return BigInt(Math.trunc(numericValue)); // Truncate and convert to BigInt
               });
               return arrow.vectorFromArray(int64Array, new arrow.Int64());
            }
            case "Float64": {
               const float64Array = data.map((value) => {
                  if (value === null || value === undefined) {
                     return null; // Handle null or undefined as-is
                  }
                  if (isNaN(Number(value))) {
                     return null; // Handle invalid non-numeric values
                  }
                  return value;
               });
               return arrow.vectorFromArray(float64Array, new arrow.Float64());
            }
            case "Timestamp<MILLISECOND>": {
               const timestampArray = data.map((value) => {
                  if (value === null || value === undefined) {
                     return null; // Handle null or undefined as-is
                  }
                  // Convert value to a Unix timestamp in milliseconds
                  const dateObj = new Date(value); // Convert to a Date object
                  if (isNaN(dateObj.getTime())) {
                     return null; // Handle invalid dates
                  }
                  return dateObj.getTime(); // Return time in milliseconds
               });
               return arrow.vectorFromArray(
                  timestampArray,
                  new arrow.TimestampMillisecond(),
               );
            }
            default: {
               const defaultArray = data.map((value) =>
                  value === null || value === undefined ? null : value,
               );
               return arrow.vectorFromArray(defaultArray);
            }
         }
      });

      try {
         // Dynamically construct the input object for tableFromArrays, excluding 'row' column
         const tableInput = columns.reduce((acc, col, idx) => {
            if (col.name !== "row") {
               acc[col.name] = arrowArrays[idx];
            }
            return acc;
         }, {});
         // Create the table with dynamically structured columns
         const table = arrow.tableFromArrays(tableInput);
         // Serialize the table to Arrow IPC format
         const writer = arrow.RecordBatchStreamWriter.writeAll(table);
         const buffer = await writer.toUint8Array();
         writer.close();
         // Convert buffer to base64 string
         const base64Encoded = btoa(String.fromCharCode(...buffer));
         const key_vals = [
            {
               key: "spreadsheet_edit_data",
               value: {
                  arrowIPCData: base64Encoded,
               },
            },
         ];
         const debouncedUpdateServerSession = debounce(
            updateServerSession,
            400,
         );
         debouncedUpdateServerSession(props, key_vals);
      } catch (error) {
         console.error("Error creating Arrow IPC data:", error);
      }
   }

   // Send request and initialize table data
   useEffect(() => {
      const rootId = props.headlist.at(-1);
      const sessionId = props.flatDoc[rootId].session;
      endpointRequest(
         props.parentObject,
         props.block.id,
         sessionId,
         props.endpoint,
      )
         .then((res) => {
            // removes any initial null values inside of a row and replaces them with an empty string
            let resArray = res.rows.map((innerArray) => {
               return innerArray.map((val) => (val === null ? "" : val));
            });
            let newRowData = resArray.map((r) => {
               return {
                  row: r.slice(1),
               };
            });
            const columnOrder = res.columns.slice(1).map((col) => col.name);
            const tableSettings = {
               tableHeight: 200,
               columnOrder: columnOrder,
               rowHeight: 40,
               noVirtualization: true,
            };
            setRowData(newRowData);
            setTableSettings(tableSettings);
            setColumns(res.columns.slice(1));
            setLoading(false);
         })
         .catch((error) => {
            console.error(error);
            setError(translate("Error loading table"));
         });
   }, []);

   let rowActions = null;

   //Only need these row actions if row count is unlocked
   if (!props.block.data.rowCountLocked) {
      rowActions = {
         rowActions: [
            {
               label: translate("Add Row Above"),
               action: (rowId) => {
                  // Creates a new row with the proper inputs from each row
                  const newRow = {
                     row: columns.map((r) => {
                        switch (r.typeId) {
                           case arrow.Type.Utf8: {
                              return "";
                           }
                           case arrow.Type.Int: {
                              return 0;
                           }
                           case arrow.Type.Float: {
                              return 0;
                           }
                           case arrow.Type.Timestamp: {
                              return new Date();
                           }
                           default: {
                              console.error(
                                 `Unsupported typeId: ${props.typeId}`,
                              );
                              break;
                           }
                        }
                     }),
                  };
                  const newRows = [...rowData];
                  newRows.splice(rowId, 0, newRow);
                  setRowData(newRows);
                  handleEditCell(newRows, columns);
               },
            },
            {
               label: translate("Add Row Below"),
               action: (rowId) => {
                  // Creates a new row with the proper inputs from each row
                  const newRow = {
                     row: columns.map((r) => {
                        switch (r.typeId) {
                           case arrow.Type.Utf8: {
                              return "";
                           }
                           case arrow.Type.Int: {
                              return 0;
                           }
                           case arrow.Type.Float: {
                              return 0;
                           }
                           case arrow.Type.Timestamp: {
                              return new Date();
                           }
                           default: {
                              console.error(
                                 `Unsupported typeId: ${props.typeId}`,
                              );
                              break;
                           }
                        }
                     }),
                  };
                  const newRows = [...rowData];
                  newRows.splice(rowId + 1, 0, newRow);
                  setRowData(newRows);
                  handleEditCell(newRows, columns);
               },
            },
            {
               label: translate("Delete"),
               action: (rowId) => {
                  const newRows = [...rowData];
                  newRows.splice(rowId, 1);
                  setRowData(newRows);
                  handleEditCell(newRows, columns);
               },
            },
         ],
      };
   }

   //Adjust Size based on container size
   useLayoutEffect(() => {
      const tableContainer = document.querySelector("#spreadsheet-block");
      if (tableContainer) {
         setTableContainerWidth(tableContainer.getBoundingClientRect().width);
      }

      //Buncha resizing garbage
      const resizeFunc = debounce(() => {
         const tableContainer = document.querySelector("#spread-sheet-table");
         if (tableContainer) {
            setTableContainerWidth(
               tableContainer.getBoundingClientRect().width - 20,
            );
         }
      }, 500);

      //resize if we resize the window
      window.addEventListener("resize", resizeFunc);
      return () => {
         window.removeEventListener("resize", resizeFunc);
         const appWidthContainer = document.querySelector(
            "#app-width-container",
         );
         if (appWidthContainer) {
            appWidthContainer.removeEventListener("widthToggled", resizeFunc);
         }
      };
   }, [tableContainerWidth]);

   if (!tableSettings || !rowData || loading || error) {
      return <ECLLoader message={error} />;
   }
   return (
      <div id="spread-sheet-table">
         <ECLTable
            handleEditCell={handleEditCell}
            rowData={rowData}
            tableSettings={{ ...tableSettings, ...rowActions }}
            columnSettings={columns.map((col, columnIndex) => {
               let cellWidth = 0;
               let shownColCount = tableSettings.columnOrder.length;
               cellWidth = tableContainerWidth / shownColCount;
               cellWidth = cellWidth < 150 ? 150 : cellWidth - 1;
               return {
                  id: col.name,
                  width: cellWidth,
                  label: col.name,
                  typeId: col.type.typeId,
                  customFormatting: (index, value) => {
                     return (
                        <EditableTableCell
                           columnIndex={columnIndex}
                           rowIndex={index}
                           columns={columns}
                           rows={rowData}
                           // table={res.table}
                           typeId={col.type.typeId}
                           handleEditCell={handleEditCell}
                           content={value}
                           testRows={rowData}
                        />
                     );
                  },
               };
            })}
         />
      </div>
   );
}
